import { computed } from 'vue'
import { defineStore } from 'pinia'
import { useQueryCache } from '@pinia/colada'
import useSystemData from './composables/useSystemData'
import type { ID, PositionModel } from '@trading/core'
import { NetworkError, type PositionWSUpdate } from '@trading/infrastructure'
import {
  POSITIONS_QUERY_KEY,
  createPositionsQueryOptions,
  getPositionsQueryKey
} from '~/queries/positions'

export const usePositionStore = defineStore('position', () => {
  const accountStore = useAccountStore()
  const queryCache = useQueryCache()
  const toast = useToast()
  const { t } = useI18n()

  const { isActual, isLoadding } = useSystemData()

  function getWsPositionService() {
    return useNuxtApp().$wsPositionService
  }

  function getCurrentAccountId(): ID | null {
    return accountStore.currentAccount?.id ?? null
  }

  const allPositions = computed(() => {
    const accountId = getCurrentAccountId()

    return accountId == null
      ? []
      : (queryCache.getQueryData<PositionModel[]>(getPositionsQueryKey(accountId)) ?? [])
  })

  function resetPositionsQueryCache() {
    queryCache.cancelQueries({
      key: [POSITIONS_QUERY_KEY]
    })

    queryCache
      .getEntries({
        key: [POSITIONS_QUERY_KEY]
      })
      .forEach((entry) => {
        queryCache.remove(entry)
      })
  }

  async function subscribeWs() {
    return getWsPositionService().subscribeToPositionsChannel(
      'update',
      (data: PositionWSUpdate) => {
        console.log('Received position update:', data)
        updatePosition(data.position, data.event)
      }
    )
  }

  async function unsubscribe() {
    getWsPositionService().unsubscribe()
  }

  async function fetchAllPosition(
    accountId: ID | null = getCurrentAccountId(),
    options: { force?: boolean } = {}
  ): Promise<PositionModel[]> {
    if (accountId == null) {
      isActual.value = false
      return []
    }

    const { force = false } = options

    isLoadding.value = true

    try {
      const key = getPositionsQueryKey(accountId)
      const entry = queryCache.ensure(createPositionsQueryOptions(accountId))

      const state = force ? await queryCache.fetch(entry) : await queryCache.refresh(entry)

      if (state.status === 'error') {
        throw state.error
      }

      isActual.value = true

      return queryCache.getQueryData<PositionModel[]>(key) ?? []
    } finally {
      isLoadding.value = false
    }
  }

  function updatePosition(position: PositionModel, event: PositionWSUpdate['event']) {
    const key = getPositionsQueryKey(position.account_id)
    const cachedPositions = queryCache.getQueryData<PositionModel[]>(key)
    if (!Array.isArray(cachedPositions)) return

    const positions = [...cachedPositions]
    const index = positions.findIndex((item) => item.id === position.id)

    if (event === 'created' && index === -1) {
      positions.push(position)
    } else if (event === 'updated' && index !== -1) {
      positions[index] = position
    } else if ((event === 'closed' || event === 'deleted') && index !== -1) {
      positions.splice(index, 1)
    }

    queryCache.setQueryData(key, positions)
  }

  async function closePosition(id: ID): Promise<void> {
    const accountId = getCurrentAccountId()
    await getWsPositionService().closePosition(id)
    if (accountId == null) return

    const key = getPositionsQueryKey(accountId)
    const positions = queryCache.getQueryData<PositionModel[]>(key)
    if (!Array.isArray(positions)) return

    queryCache.setQueryData(
      key,
      positions.filter((position) => position.id !== id)
    )
  }

  async function doublePosition(position: PositionModel | null): Promise<void> {
    if (!position) return

    try {
      await getWsPositionService().doublePosition(position)
    } catch (e: unknown) {
      let description = ''
      if (e instanceof NetworkError) {
        description = e.message
      } else {
        description = String(t('positionsTable.doublePositionsActionFail'))
      }
      toast.add({ title: String(t('common.error')), description, color: 'error' })
      return
    }
    toast.add({ title: String(t('positionsTable.modalDoublePosition.success')), color: 'success' })
  }

  async function reversePosition(position: PositionModel | null): Promise<void> {
    if (!position) return

    try {
      await getWsPositionService().reversePosition(position)
    } catch (e: unknown) {
      let description = ''
      if (e instanceof NetworkError) {
        description = e.message
      } else {
        description = String(t('positionsTable.reversePositionsActionFail'))
      }
      toast.add({ title: String(t('common.error')), description, color: 'error' })
      return
    }
    toast.add({ title: String(t('positionsTable.modalReversePosition.success')), color: 'success' })
  }

  async function modifyPosition(position: PositionModel | null): Promise<void> {
    if (!position) return

    try {
      await getWsPositionService().modifyPosition(position)
    } catch (e: unknown) {
      let description = ''
      if (e instanceof NetworkError) {
        description = e.message
      } else {
        description = String(t('positionsTable.modifyPositionsActionFail'))
      }
      toast.add({ title: String(t('common.error')), description, color: 'error' })
      return
    }
    toast.add({ title: String(t('positionsTable.modalModifyPosition.success')), color: 'success' })
  }

  function reset() {
    isActual.value = false
    isLoadding.value = false

    resetPositionsQueryCache()
  }

  return {
    modifyPosition,
    doublePosition,
    reversePosition,
    fetchAllPosition,
    allPositions,
    closePosition,
    isLoadding,
    updatePosition,
    subscribeWs,
    unsubscribe,
    reset
  }
})
